import React from "react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import ProcedurePlaceholder from "@/components/ProcedurePlaceholder";
import { HERO } from "@/data/procedureHeroImages";
import PatientTestimonials from "../components/PatientTestimonials";
import SEO from "@/components/SEO";
import { WHATSAPP_URL, TEL_URL, PHONE_DISPLAY } from "@/constants/contact";
import {
  ArrowRight,
  Award,
  BedDouble,
  ChevronRight,
  Clock,
  GraduationCap,
  HeartPulse,
  MessageCircle,
  Phone,
  Minus,
  Monitor,
  Plus,
  Shield,
  Sparkles,
  Syringe,
  TrendingUp,
  UserRound,
  Activity,
} from "lucide-react";

const PAGE_URL = "https://drvickyjain.com/lipoma-removal-surgery-mumbai";

const LIPOMA_FAQS = [
  {
    question: "Is a lipoma dangerous?",
    answer:
      "In the overwhelming majority of cases, no. Lipomas are benign fatty growths that do not invade surrounding tissue and do not spread. The risk of a lipoma transforming into a malignant tumor is extremely low. However, not every soft lump under the skin is automatically a lipoma. Clinical examination and ultrasound confirm the diagnosis. Any lump with hard consistency, rapid growth, fixation to deep tissue, or overlying skin change requires prompt assessment regardless of how long it has been present.",
  },
  {
    question: "Can a lipoma go away on its own?",
    answer:
      "No. Lipomas do not resolve spontaneously. They may remain stable in size for years, but they do not disappear without treatment. Some very small lipomas may be difficult to feel at times if they shift position, creating the impression of resolution, but the growth remains present. Permanent removal requires surgical excision or liposuction-assisted removal.",
  },
  {
    question: "Does lipoma removal leave a scar?",
    answer:
      "Yes, but the scar is small. The incision for most lipoma removals is a few centimeters at most and is placed in the most cosmetically favorable position for the specific location. Closure with layered fine sutures produces a flat, narrow scar that fades to near-invisible in most patients within 6 to 12 months with appropriate scar care.",
  },
  {
    question: "Can a lipoma come back after removal?",
    answer:
      "Recurrence after complete surgical excision is uncommon. When the lipoma is removed with its surrounding capsule intact, there is minimal residual tissue from which recurrence can develop. Incomplete removal, leaving capsule or fatty tissue behind, increases recurrence risk. Patients with familial multiple lipomatosis may develop new lipomas at different sites after removal of existing ones, which is a new growth rather than a recurrence of the removed lipoma.",
  },
  {
    question: "Is lipoma removal painful?",
    answer:
      "The procedure is performed under local anesthesia. The anesthesia injections produce brief discomfort that resolves quickly. The removal itself is painless. Post-procedure mild soreness at the site for 2 to 3 days is normal and managed with standard pain relief.",
  },
  {
    question: "Can multiple lipomas be removed in one session?",
    answer:
      "Yes. Multiple lipomas can be removed in a single session under local anesthesia when their locations are accessible and the total procedure time remains within a reasonable limit for a local anesthesia procedure. Dr. Jain discusses which lipomas are best addressed together and which may require separate sessions based on their locations and sizes during consultation.",
  },
  {
    question: "What is the difference between a lipoma and a cyst?",
    answer:
      "A lipoma is a growth of fatty tissue beneath the skin. It feels soft, doughy, and moves freely. A sebaceous cyst is a sac filled with keratin material that develops from a blocked skin gland. Cysts tend to feel firmer than lipomas, are more superficial in the skin rather than the subcutaneous fat, and often have a visible central punctum or opening. Both are benign and both are treated by surgical excision, but the surgical technique differs. Ultrasound distinguishes between them when clinical examination is inconclusive.",
  },
  {
    question: "Do I need to fast or prepare specially for lipoma removal?",
    answer:
      "No special preparation is required for lipoma removal under local anesthesia. Patients can eat and drink normally before the procedure. Patients taking blood thinners should inform Dr. Jain during consultation as some anticoagulants may need to be paused before surgery depending on the medication and the clinical situation. No hospital admission, pre-operative blood tests, or fasting is required for a straightforward local anesthesia lipoma excision.",
  },
];

const structuredData = {
  "@context": "https://schema.org",
  "@graph": [
    {
      "@type": "MedicalCondition",
      name: "Lipoma",
      description:
        "Benign subcutaneous fatty growth; diagnosed clinically and with imaging; managed with observation or surgical removal depending on symptoms, growth, and patient preference.",
      associatedAnatomy: { "@type": "AnatomicalStructure", name: "Subcutaneous tissue" },
      possibleTreatment: [
        "Observation with periodic clinical monitoring",
        "Surgical excision with histological analysis",
        "Liposuction-assisted removal",
        "Injection lipolysis (select cases)",
      ],
      url: PAGE_URL,
    },
    {
      "@type": "MedicalProcedure",
      name: "Lipoma excision",
      description:
        "Local anesthesia removal of a lipoma with intact capsule, layered closure, and specimen histology under plastic surgical care.",
      procedureType: "SurgicalProcedure",
      bodyLocation: { "@type": "AnatomicalStructure", name: "Subcutaneous tissue" },
      medicalSpecialty: "https://schema.org/PlasticSurgery",
      url: PAGE_URL,
    },
    {
      "@type": "FAQPage",
      mainEntity: LIPOMA_FAQS.map((faq) => ({
        "@type": "Question",
        name: faq.question,
        acceptedAnswer: { "@type": "Answer", text: faq.answer },
      })),
    },
  ],
};

function LipomaJsonLd() {
  return <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(structuredData) }} />;
}

function Breadcrumb() {
  const items = [
    { name: "Home", href: "/" },
    { name: "Reconstructive Surgery", href: "/reconstructive-surgery-in-mumbai" },
    { name: "Lipoma Removal", href: "/lipoma-removal-surgery-mumbai" },
  ];
  return (
    <nav className="bg-warm-white pt-24 pb-4" data-testid="breadcrumb">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <ol className="flex flex-wrap items-center gap-2 text-sm text-deep-navy/60">
          {items.map((item, index) => (
            <li key={item.name} className="flex items-center gap-2">
              {index > 0 && <ChevronRight className="w-3 h-3 text-gold" />}
              {index === items.length - 1 ? (
                <span className="text-deep-navy/70">{item.name}</span>
              ) : (
                <Link to={item.href} className="hover:text-gold transition-colors">
                  {item.name}
                </Link>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
}

function HeroSection() {
  return (
    <section className="bg-warm-white pt-8 md:pt-10 pb-12 md:pb-20" data-testid="procedure-hero">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Soft Tissue</p>
            <h1 className="font-serif text-4xl sm:text-5xl md:text-6xl text-premium-purple leading-[1.08] mb-5">Lipoma Removal</h1>
            <div className="w-12 h-[2px] bg-gold mb-5" />
            <p className="text-lg text-deep-navy/70 mb-8">
              Safe &amp; Permanent Fat Lump Removal by Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon
            </p>
            <div className="flex flex-wrap gap-3">
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-medium text-premium-purple">Board Certified</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-medium text-premium-purple">17+ Years Experience</span>
              <span className="bg-premium-purple/10 px-3 py-1.5 rounded-full text-xs font-medium text-premium-purple">International Training</span>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <ProcedurePlaceholder procedureName="Lipoma Removal" icon="🔬" size="hero"  imageSrc={HERO.lipomaRemoval} imageAlt="Lipoma removal"/>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhatIsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="what-is-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Overview</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">What is a Lipoma?</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              A lipoma is a benign, non-cancerous growth of fatty tissue that develops just beneath the skin. It is one of the most common soft tissue growths in adults and is almost always harmless. Lipomas feel soft and doughy to the touch, move easily when pressed, and grow slowly over months or years. They are not cancer and do not become cancer in the overwhelming majority of cases.
            </p>
            <p>
              Most lipomas are discovered incidentally, either by the patient or during a clinical examination for another reason. They cause no symptoms in most cases. The reasons patients seek treatment are cosmetic concern when the lipoma is visible, discomfort when it presses on surrounding tissue or nerves, anxiety about what the lump is, or growth that has made the lipoma noticeably larger over time.
            </p>
            <p>
              Dr. Vicky Jain removes lipomas through surgical excision under local anesthesia. The procedure takes 20 to 40 minutes and requires no hospital admission. The excised tissue is sent for histological analysis to confirm the diagnosis. Results are permanent when the lipoma is completely removed.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function DrJainApproachSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="dr-jain-approach-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Surgical Approach</p>
            <h2 className="font-serif text-3xl md:text-4xl text-white mb-8">Dr. Jain&apos;s Approach to Lipoma Removal</h2>
            <div className="space-y-6 text-white/75 leading-relaxed">
              <p>
                The primary decision in lipoma treatment is whether surgical excision is needed at all. Small, asymptomatic lipomas with a confident clinical diagnosis can be safely observed. Dr. Jain does not automatically recommend surgery for every lipoma. The decision is based on size, location, symptoms, whether the diagnosis is certain, and the patient&apos;s own concerns. A patient who is worried about the lump and wants it removed has a valid reason for surgery even if the lipoma is causing no physical symptoms.
              </p>
              <p>
                When surgery is performed, complete removal is the goal. Incomplete removal, leaving behind a portion of the lipoma capsule or fatty tissue, results in recurrence at the same site. Dr. Jain excises the lipoma with its surrounding capsule intact to ensure complete removal and minimize recurrence risk. The incision is placed in the most cosmetically favorable position for each location, and the closure uses layered sutures to produce a flat, narrow scar that fades over time.
              </p>
            </div>
          </div>
          <div className="relative">
            <img src="/dr-jain.avif" alt="Dr. Vicky Jain" loading="lazy" decoding="async" className="w-full h-[420px] object-cover object-top" />
            <div
              className="absolute top-0 left-0 right-0 h-[420px] pointer-events-none hidden lg:block"
              style={{ background: "linear-gradient(to right, #3D2566 0%, rgba(61, 37, 102, 0) 15%)" }}
            />
            <div className="mt-4 text-center lg:text-left">
              <p className="font-serif text-lg text-white/80">Dr. Vicky Jain · Plastic &amp; Reconstructive Surgeon</p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function WhenTreatmentSection() {
  const cards = [
    {
      title: "Increasing Size",
      icon: TrendingUp,
      copy: "A lipoma that was previously stable and has started growing warrants clinical assessment and consideration for removal. Growth changes the risk-benefit calculation for observation versus excision.",
    },
    {
      title: "Pain or Pressure",
      icon: HeartPulse,
      copy: "Most lipomas are painless. A lipoma that causes consistent local pain or presses on a nerve, creating radiating discomfort, is an indication for removal regardless of size.",
    },
    {
      title: "Cosmetic Concern",
      icon: UserRound,
      copy: "A lipoma that is visible under clothing or creates a noticeable bulge in an aesthetically prominent location is a valid indication for removal. Cosmetic concern is a legitimate reason for elective surgery and does not require a physical symptom to justify treatment.",
    },
    {
      title: "Diagnostic Uncertainty",
      icon: Syringe,
      copy: "When clinical examination and imaging are not conclusive that the lump is a benign lipoma, surgical excision with histological analysis of the specimen provides a definitive diagnosis. This is the most important indication for removal when the diagnosis is in question.",
    },
    {
      title: "Restricted Movement",
      icon: Activity,
      copy: "Lipomas located near joints or in areas where they create mechanical restriction during movement are removed to restore normal function.",
    },
    {
      title: "Patient Anxiety",
      icon: Shield,
      copy: "Persistent concern about a confirmed benign lipoma is a valid reason for elective removal. Living with ongoing anxiety about a lump that can be safely removed is not a requirement of the observation approach.",
    },
  ];
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="when-treatment-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Indications</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">When Does a Lipoma Need to Be Removed?</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {cards.map((card) => (
            <div key={card.title} className="bg-white p-8 border border-[#E8E2D9]">
              <div className="w-12 h-12 bg-gold/10 flex items-center justify-center mb-4" aria-hidden>
                <card.icon className="w-6 h-6 text-gold" />
              </div>
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{card.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{card.copy}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

const VS_SUSPICIOUS_ROWS = [
  {
    label: "Consistency",
    lipoma: "Soft and doughy. Compresses easily under pressure.",
    suspicious: "Hard or firm. Does not compress.",
  },
  {
    label: "Mobility",
    lipoma: "Moves freely under the skin when pressed.",
    suspicious: "Fixed to underlying tissue. Does not move independently.",
  },
  {
    label: "Growth Rate",
    lipoma: "Slow. Often unchanged over months or years.",
    suspicious: "Rapid growth over weeks.",
  },
  {
    label: "Pain",
    lipoma: "Usually painless. Occasionally tender if pressing on a nerve.",
    suspicious: "May be painful or tender independent of pressure.",
  },
  {
    label: "Skin Changes",
    lipoma: "No skin changes over the lump.",
    suspicious: "Skin dimpling, redness, or ulceration over the lump.",
  },
  {
    label: "Action Required",
    lipoma: "Elective assessment and treatment at your convenience.",
    suspicious: "Prompt medical assessment. Do not delay.",
  },
];

function LipomaVsSuspiciousSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="lipoma-vs-suspicious-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Understanding Your Diagnosis</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">
          Lipoma vs. Suspicious Lump: When to Seek Urgent Assessment
        </h2>
        <div className="max-w-5xl mx-auto overflow-x-auto">
          <table className="w-full text-left border-collapse text-sm md:text-base">
            <thead>
              <tr className="border-b border-deep-navy/20">
                <th scope="col" className="py-4 pr-4 font-serif text-premium-purple font-normal w-[18%]" />
                <th scope="col" className="py-4 px-3 font-serif text-premium-purple bg-white/80">
                  Lipoma
                </th>
                <th scope="col" className="py-4 pl-3 font-serif text-premium-purple bg-white/80">
                  Suspicious Lump
                </th>
              </tr>
            </thead>
            <tbody>
              {VS_SUSPICIOUS_ROWS.map((row) => (
                <tr key={row.label} className="border-b border-deep-navy/10">
                  <th scope="row" className="py-4 pr-4 font-medium text-premium-purple align-top">
                    {row.label}
                  </th>
                  <td className="py-4 px-3 text-deep-navy/70 align-top">{row.lipoma}</td>
                  <td className="py-4 pl-3 text-deep-navy/70 align-top">{row.suspicious}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="max-w-5xl mx-auto mt-8 text-deep-navy/70 text-center md:text-left leading-relaxed">
          Clinical examination alone cannot definitively distinguish a lipoma from other soft tissue growths. Ultrasound confirms the diagnosis in most cases. Any lump with hard consistency, fixation to deep tissue, rapid growth, or overlying skin change requires urgent medical assessment regardless of how long it has been present.
        </p>
      </div>
    </section>
  );
}

function CommonLocationsSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="common-locations-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">Common Sites</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">Where Lipomas Commonly Occur</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Lipomas develop in the subcutaneous fatty tissue beneath the skin and can technically occur anywhere on the body where subcutaneous fat is present. They are most commonly found on the back, shoulders, neck, upper arms, thighs, and abdomen. Multiple lipomas occurring simultaneously or in a familial pattern are called familial multiple lipomatosis, a genetic condition where individuals develop numerous lipomas across the body over time.
            </p>
            <p>
              The location of a lipoma affects both the surgical approach and the cosmetic outcome. Lipomas on the back and shoulders are removed through small incisions that heal well in areas with good skin quality. Lipomas on the face, neck, and hands require more precise technique due to proximity to nerves and vessels and greater cosmetic sensitivity. Lipomas overlying joints or located deep to muscle may require more extensive dissection than superficial subcutaneous lipomas. Dr. Jain assesses the location and depth of each lipoma during consultation to plan the appropriate surgical approach.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

const TREATMENT_OPTIONS = [
  {
    title: "Observation",
    body:
      "Appropriate for small, asymptomatic lipomas with a confident clinical diagnosis. Regular monitoring at 6 to 12 month intervals confirms that the lipoma remains stable. No treatment is required unless symptoms develop, growth occurs, or the patient requests removal. Many lipomas remain stable for years without requiring intervention.",
  },
  {
    title: "Surgical Excision",
    body:
      "The gold standard treatment and the most definitive option. A small incision is made over the lipoma, the fatty growth is removed with its surrounding capsule intact, and the incision is closed with layered sutures. The excised specimen is sent for histological analysis. Results are permanent when removal is complete. Very low recurrence rate with complete excision. Appropriate for lipomas of any size, symptomatic lipomas, and any case where diagnostic confirmation is needed.",
  },
  {
    title: "Liposuction-Assisted Removal",
    body:
      "A specialized liposuction cannula is inserted through a small access point and the lipoma is aspirated. Produces a smaller scar than open excision and is appropriate for larger, softer lipomas in favorable locations. The trade-off is a slightly higher recurrence rate than open excision because complete capsule removal is not always achievable through the liposuction approach. Used selectively for appropriate candidates.",
  },
  {
    title: "Injection Lipolysis",
    body:
      "Fat-dissolving injections are occasionally used for very small lipomas. Results are inconsistent and incomplete removal is common. Not the preferred approach when definitive removal is the goal.",
  },
];

function TreatmentOptionsSection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="treatment-options-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Procedure Options</p>
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-10 text-center">Lipoma Treatment Options</h2>
        <div className="grid grid-cols-1 lg:grid-cols-1 gap-8 max-w-4xl mx-auto">
          {TREATMENT_OPTIONS.map((item) => (
            <div key={item.title} className="bg-white p-8 border border-[#E8E2D9]">
              <h3 className="font-serif text-xl text-premium-purple mb-4 font-semibold tracking-tight">{item.title}</h3>
              <p className="text-deep-navy/70 leading-relaxed">{item.body}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

function HowSurgerySection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="how-surgery-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid lg:grid-cols-[1fr_2fr] gap-10 md:gap-16 items-start">
          <div>
            <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3">The Procedure</p>
            <h2 className="font-serif text-3xl md:text-4xl text-premium-purple">How Lipoma Removal Surgery is Performed</h2>
          </div>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Surgery takes 20 to 40 minutes under local anesthesia. No hospital admission is required. Dr. Jain marks the lipoma position before the procedure and plans the incision placement to minimize scar visibility for that specific location.
            </p>
            <p>
              After local anesthesia is administered, a small incision is made directly over the lipoma. The lipoma is dissected from the surrounding tissue with its capsule intact and removed completely. The wound is closed in two layers: the deep tissue is approximated first to eliminate dead space beneath the skin, and the skin is closed with fine sutures. A simple dressing is applied.
            </p>
            <p>
              The excised lipoma is sent for histological analysis at every case. Most results confirm the benign lipoma diagnosis. In the rare instance where histology reveals an unexpected finding, the result is reviewed with the patient and further management is discussed. Histological confirmation provides diagnostic certainty that clinical examination and imaging alone cannot guarantee.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function RecoveryTimelineSection() {
  const milestones = [
    {
      period: "Days 1 to 3",
      description:
        "Mild pain and swelling at the removal site. Normal and expected. Managed with standard pain medication. A simple dressing covers the wound.",
      icon: BedDouble,
    },
    {
      period: "Days 7 to 10",
      description:
        "Sutures are removed at the post-operative visit. The wound is healing cleanly. Mild bruising may still be present.",
      icon: Monitor,
    },
    {
      period: "Week 2",
      description:
        "Most patients resume full normal activity. The wound is closed and healed. Scar care begins at this point with silicone gel or sheeting to improve long-term scar appearance.",
      icon: Clock,
    },
    {
      period: "Months 1 to 6",
      description:
        "The scar matures and fades. Most small lipoma scars are difficult to see within 6 months. The result is permanent with no further treatment required.",
      icon: Sparkles,
    },
  ];
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="recovery-timeline-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <h2 className="font-serif text-3xl md:text-4xl text-white mb-12 text-center">Recovery Timeline</h2>
        <div className="hidden md:block relative">
          <div className="absolute top-16 left-0 right-0 h-0.5 bg-deep-navy/10" />
          <div className="grid grid-cols-4 gap-4">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-milestone-${index}`}>
                <div className="flex justify-center mb-3">
                  <milestone.icon className="w-6 h-6 text-deep-navy/40" />
                </div>
                <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center mx-auto mb-4 relative z-10">
                  <div className="w-3 h-3 rounded-full bg-warm-white" />
                </div>
                <div className="text-center">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="md:hidden relative pl-12">
          <div className="absolute top-0 bottom-0 left-4 w-0.5 bg-deep-navy/10" />
          <div className="space-y-8">
            {milestones.map((milestone, index) => (
              <div key={milestone.period} className="relative" data-testid={`timeline-mobile-${index}`}>
                <div className="absolute -left-12 top-0 flex flex-col items-center">
                  <milestone.icon className="w-5 h-5 text-deep-navy/40 mb-1" />
                  <div className="w-8 h-8 rounded-full bg-gold flex items-center justify-center">
                    <div className="w-3 h-3 rounded-full bg-warm-white" />
                  </div>
                </div>
                <div className="pl-4">
                  <h4 className="font-serif text-lg text-premium-purple mb-2">{milestone.period}</h4>
                  <p className="text-sm text-deep-navy/60 leading-relaxed">{milestone.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function WhoShouldConsiderSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="who-should-consider-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="max-w-4xl mx-auto">
          <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Am I a Candidate?</p>
          <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-8 text-center">Who Should Consider Lipoma Removal?</h2>
          <div className="space-y-6 text-deep-navy/70 leading-relaxed">
            <p>
              Any adult with a confirmed or suspected lipoma who has a specific reason for wanting it removed is an appropriate candidate for consultation. Surgical removal does not require a minimum size, a minimum symptom threshold, or a specific location. The indication can be purely cosmetic, purely symptomatic, or simply to obtain a definitive diagnosis and eliminate uncertainty. There is no advantage to leaving a lipoma that is causing concern if the removal is straightforward.
            </p>
            <p>
              Patients with multiple lipomas should discuss all of them during consultation rather than treating one at a time without a coordinated plan. Some patients with multiple lipomas benefit from treating several in a single session under local anesthesia, which avoids multiple separate recovery periods. Patients with a family history of lipomatosis should have all new lumps assessed rather than assuming each new growth is a benign lipoma without confirmation. Patients in good general health with no active infections overlying the lump are appropriate for local anesthesia excision in a clinic setting without any special preparation.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
}

function PatientResultsSection() {
  return <PatientTestimonials procedureName="Lipoma" />;
}

function FAQSection() {
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="faq-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Common Questions</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Frequently Asked Questions</h2>
        <div className="max-w-3xl mx-auto">
          <Accordion type="single" collapsible className="w-full">
            {LIPOMA_FAQS.map((faq, index) => (
              <AccordionItem key={faq.question} value={`lipoma-${index}`} className="border-b border-deep-navy/10">
                <AccordionTrigger className="text-left font-serif text-lg text-premium-purple hover:no-underline hover:text-gold py-6 [&[data-state=open]>div>svg:first-child]:hidden [&[data-state=open]>div>svg:last-child]:block [&>svg]:hidden">
                  <span className="pr-4">{faq.question}</span>
                  <div className="flex-shrink-0">
                    <Plus className="w-5 h-5 text-gold" />
                    <Minus className="w-5 h-5 text-gold hidden" />
                  </div>
                </AccordionTrigger>
                <AccordionContent className="text-deep-navy/70 leading-relaxed pb-6">{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </div>
    </section>
  );
}

function ConsultationCTASection() {
  return (
    <section className="bg-premium-purple py-10 md:py-14" data-testid="consultation-cta-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl text-center">
        <h2 className="font-serif text-3xl md:text-4xl text-warm-white mb-4">Book Your Lipoma Removal Consultation</h2>
        <p className="text-warm-white/70 mb-8 max-w-2xl mx-auto leading-relaxed">
          During your consultation, Dr. Jain examines the lump, arranges ultrasound if needed to confirm the diagnosis, and advises on whether observation or surgical removal is appropriate for your specific lipoma. If removal is planned, the incision position and closure technique are discussed so you know exactly what the scar will look like and where it will sit.
        </p>
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-6">
          <Button
            asChild
            className="bg-gold text-white hover:bg-gold-hover border-none rounded-none px-8 py-6 text-base"
            data-testid="cta-book-consultation"
          >
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer">
              Book Consultation
              <ArrowRight className="w-5 h-5 ml-2" />
            </a>
          </Button>
          <Button
          asChild
          variant="outline"
          className="border-warm-white text-warm-white hover:bg-warm-white hover:text-deep-navy rounded-none px-8 py-6 text-base"
          data-testid="cta-call"
        >
          <a href={TEL_URL}>
            <Phone className="w-5 h-5 mr-2" />
            Call {PHONE_DISPLAY}
          </a>
        </Button>
        </div>
      </div>
    </section>
  );
}

function RelatedProceduresSection() {
  const procedures = [
    {
      name: "Fibroadenoma Treatment",
      description: "Benign breast lumps requiring similar diagnosis, monitoring, and surgical removal assessment.",
      href: "/fibroadenoma-breast-lump-treatment-mumbai",
    },
    {
      name: "Axillary Breast Removal",
      description: "Removal of accessory breast tissue that is sometimes confused with lipomas in the underarm area.",
      href: "/axillary-breast-removal-mumbai",
    },
    {
      name: "Liposuction",
      description: "Fat removal for body contouring in patients with generalized fat deposits alongside localized lipomas.",
      href: "/liposuction-surgery-mumbai",
    },
  ];
  return (
    <section className="bg-warm-white py-14 md:py-20 border-t border-deep-navy/[0.06]" data-testid="related-procedures-section">
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <p className="text-xs font-bold tracking-[0.2em] text-gold uppercase mb-3 text-center">Related Procedures</p>
        <h2 className="font-serif text-3xl md:text-4xl text-premium-purple mb-10 text-center">Related Procedures</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {procedures.map((procedure) => (
            <div
              key={procedure.name}
              className="bg-white p-8 border border-[#E8E2D9] hover:shadow-lg transition-shadow duration-300 group"
            >
              <h3 className="font-serif text-xl text-premium-purple mb-3 font-semibold tracking-tight">{procedure.name}</h3>
              <p className="text-deep-navy/60 mb-6 leading-relaxed">{procedure.description}</p>
              <Link
                to={procedure.href}
                className="inline-flex items-center text-deep-navy font-medium hover:text-gold transition-colors"
              >
                Learn More →
                <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
              </Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default function LipomaRemovalPage() {
  return (
    <>
      <SEO
        title="Lipoma Removal Surgery | Dr. Vicky Jain"
        description="Soft lump under the skin growing in size or causing discomfort. Dr. Vicky Jain performs safe, permanent lipoma removal. Book your consultation today."
        canonical="/lipoma-removal-surgery-mumbai"
      />
      <LipomaJsonLd />
      <Breadcrumb />
      <HeroSection />
      <WhatIsSection />
      <DrJainApproachSection />
      <WhenTreatmentSection />
      <LipomaVsSuspiciousSection />
      <CommonLocationsSection />
      <TreatmentOptionsSection />
      <HowSurgerySection />
      <RecoveryTimelineSection />
      <WhoShouldConsiderSection />
      <PatientResultsSection />
      <FAQSection />
      <ConsultationCTASection />
      <RelatedProceduresSection />
    </>
  );
}
